import { useEffect } from "react";
import { api } from "../utils/apiClient";
import { appMessage as message } from "../utils/appMessage";
import { persistConsent, sameConsent } from "./cookieConsentStorage";
import { wasNotified } from "../utils/notifiedError";

// Reconciles local consent with the server for authenticated users.
export function useServerConsentSync({
  isAuthenticated,
  currentUserId,
  expectedVersion,
  consent,
  consentRef,
  lastSyncedRef,
  reconciledAuthKeyRef,
  setConsent,
  setBannerVisible,
  persistServerConsent,
}) {
  useEffect(() => {
    const enforceAuthenticatedConsent = async () => {
      if (!isAuthenticated || !currentUserId) return;

      const authKey = `${currentUserId}:${expectedVersion}`;
      if (reconciledAuthKeyRef.current === authKey) return;

      try {
        const { data } = await api.get("/api/cookie-consent/current");
        const serverConsent = data?.consent;

        if (serverConsent && serverConsent.consent_version === expectedVersion) {
          const normalized = {
            first_party: !!serverConsent.first_party,
            third_party: !!serverConsent.third_party,
            consent_version: serverConsent.consent_version,
            source: serverConsent.source || "server",
            updated_at: serverConsent.updated_at || new Date().toISOString(),
          };

          if (!sameConsent(consentRef.current, normalized)) {
            setConsent(normalized);
            persistConsent(normalized);
          }
          setBannerVisible(false);
          lastSyncedRef.current = `${currentUserId}:${normalized.updated_at}:${normalized.consent_version}`;
          reconciledAuthKeyRef.current = authKey;
          return;
        }

        const localConsent = consentRef.current;
        if (localConsent && localConsent.consent_version === expectedVersion) {
          await persistServerConsent(localConsent);
          lastSyncedRef.current = `${currentUserId}:${localConsent.updated_at}:${localConsent.consent_version}`;
          reconciledAuthKeyRef.current = authKey;
          return;
        }

        setBannerVisible(true);
      } catch (err) {
        if (!wasNotified(err)) message.error(err?.response?.data?.error || "Failed to validate cookie consent");
      }
    };

    enforceAuthenticatedConsent();
  }, [
    consentRef,
    currentUserId,
    expectedVersion,
    isAuthenticated,
    lastSyncedRef,
    persistServerConsent,
    reconciledAuthKeyRef,
    setBannerVisible,
    setConsent,
  ]);

  useEffect(() => {
    const syncConsent = async () => {
      if (!isAuthenticated || !currentUserId || !consent) return;
      if (consent.consent_version !== expectedVersion) return;

      const syncKey = `${currentUserId}:${consent.updated_at}:${consent.consent_version}`;
      if (lastSyncedRef.current === syncKey) return;

      try {
        await persistServerConsent(consent);
        lastSyncedRef.current = syncKey;
      } catch (err) {
        if (!wasNotified(err)) message.error(err?.response?.data?.error || "Failed to sync cookie consent");
      }
    };

    syncConsent();
  }, [
    consent,
    currentUserId,
    expectedVersion,
    isAuthenticated,
    lastSyncedRef,
    persistServerConsent,
  ]);
}
